import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AuthError, AuthLoading } from '@/components/AuthLoading';
import { prefetchBattlepassStatic } from '@/hooks/useBattlepass';
import { prefetchCalendar } from '@/hooks/useCalendar';
import { prefetchSpinState } from '@/hooks/useSpin';
import { prefetchSpecialOffers } from '@/hooks/useSpecialOffer';
import { prefetchAuction } from '@/hooks/useAuction';
import { prefetchTitleManager } from '@/hooks/useTitleManager';
import { prefetchRanking } from '@/hooks/useRanking';
import { prefetchChatbotHistory } from '@/hooks/useChatbot';
import { prefetchJobSystem } from '@/hooks/useJobSystem';
import { prefetchCupons } from '@/hooks/useCupons';
import { prefetchSettings } from '@/hooks/useSettings';
import { prefetchMessagesQueries, setAuthCharIdFallback } from '@/hooks/useMessages';
import { prefetchNotificationsQueries } from '@/hooks/useNotifications';
import { connectMessagesWebSocket } from '@/hooks/useMessagesWebSocket';
import { useAuth } from '@/hooks/useAuth';
import { t } from '@/lib/i18n';
import { SyncAccountLocale } from '@/components/SyncAccountLocale';
import { AppRouter } from '@/router';

function syncBodyAuthShell(authenticated: boolean) {
  document.body.classList.toggle('auth-boot', !authenticated);
  document.body.classList.toggle('app-ingame', authenticated);
}

export default function App() {
  const { user, player, isLoading, error, revalidate } = useAuth();
  const queryClient = useQueryClient();
  const authenticated = !isLoading && !error && Boolean(user);

  if (user) {
    setAuthCharIdFallback(user.charId);
  }

  useEffect(() => {
    syncBodyAuthShell(authenticated);
    return () => syncBodyAuthShell(false);
  }, [authenticated]);

  useEffect(() => {
    if (user) {
      setAuthCharIdFallback(user.charId);
      prefetchBattlepassStatic(queryClient);
      prefetchCalendar(queryClient);
      prefetchSpinState(queryClient);
      prefetchSpecialOffers(queryClient);
      prefetchAuction(queryClient);
      prefetchTitleManager(queryClient);
      prefetchRanking(queryClient);
      prefetchChatbotHistory(queryClient, user.charId);
      prefetchJobSystem(queryClient, user.charId);
      prefetchCupons(queryClient, user.charId);
      prefetchSettings(queryClient, user.charId);
      prefetchMessagesQueries(queryClient, user.charId);
      prefetchNotificationsQueries(queryClient, user.charId);
      connectMessagesWebSocket(user.charId);
    }
  }, [user, queryClient]);

  if (isLoading) {
    return <AuthLoading />;
  }

  if (error || !user) {
    return <AuthError message={error ?? t('auth_error')} onRetry={() => void revalidate()} />;
  }

  return (
    <>
      <SyncAccountLocale />
      <AppRouter user={user} player={player} />
    </>
  );
}
